/**
 * THE REPAIR MODEL — the heart of the game. Pure simulation, zero rendering, zero THREE,
 * zero DOM, so it can be driven headless and measured.
 *
 * PowerWash Simulator is the bar. The thing that makes PowerWash satisfying is not the
 * percentage — it is that the percentage is an AGGREGATE OF VISIBLE PARTS. You can see the
 * streak you missed, and that is what pulls you back for one more pass.
 *
 * So a pothole is a grid of CELLS, each with its own 0..1 fill, and fill01 is their mean.
 * Ring edges use equal-area bands (r_i = sqrt(i/rings)) so every cell covers exactly the
 * same area and therefore counts exactly the same. You cannot cheese the meter by only
 * doing the middle.
 *
 * The other half of the PowerWash lesson is the FAILURE mode: the last 10% drags, because
 * hunting one stray pixel of dirt is not fun. Two mechanisms kill that here:
 *   1. finish assist — above finishAssistAt the spray bar stops sweeping and homes on the
 *      least-filled cell, so the game helps you find the gap instead of hiding it.
 *   2. snap-to-complete — above snapFill with every cell over snapMinCell, we complete.
 * Together they collapse the 90→100 tail to a handful of frames. The playtest driver
 * reports `repairLastTenthRatio`; keeping it under ~0.15 is the whole point.
 */

const TAU = Math.PI * 2;
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Static geometry of the cell grid. Built once and shared by every pothole — the layout is
 * identical for all of them, only the fill values differ.
 */
export class CellGrid {
  constructor(rings, sectors) {
    this.rings = rings;
    this.sectors = sectors;
    this.count = rings * sectors;

    /** Ring EDGE radii, normalised. Equal-area: r_i = sqrt(i/rings). */
    this.edgeR = new Float32Array(rings + 1);
    for (let i = 0; i <= rings; i++) this.edgeR[i] = Math.sqrt(i / rings);

    /** Cell CENTROID position in normalised disc coordinates. */
    this.cx = new Float32Array(this.count);
    this.cy = new Float32Array(this.count);
    /** Centroid radius, kept separately — the depth profile is a function of radius. */
    this.cr = new Float32Array(this.count);

    for (let i = 0; i < rings; i++) {
      const r = Math.sqrt((i + 0.5) / rings);
      for (let j = 0; j < sectors; j++) {
        const a = ((j + 0.5) / sectors) * TAU;
        const c = i * sectors + j;
        this.cx[c] = r * Math.cos(a);
        this.cy[c] = r * Math.sin(a);
        this.cr[c] = r;
      }
    }
  }

  ringOf(cell) { return (cell / this.sectors) | 0; }
  sectorOf(cell) { return cell % this.sectors; }
}

/**
 * A live repair session. One per pothole being worked. Allocation-free after construction:
 * the owning subsystem keeps a single instance and rebinds it.
 */
export class RepairSession {
  constructor(grid) {
    this.grid = grid;
    this.potholeId = -1;
    this.cells = null;          // Float32Array owned by the pothole entity
    this.active = false;

    this.fill01 = 0;
    this.minCell = 0;
    this.quality01 = 0;

    this.aimX = 0;              // spray bar aim point, normalised disc coords
    this.aimY = 0;
    this.targetX = 0;
    this.targetY = 0;

    this.elapsed = 0;           // seconds of trigger-held time in this session
    this.sweepPhase = 0;        // integrated sweep clock, seconds
    this.delivered = 0;         // total fill units that landed in an unfilled cell
    this.wasted = 0;            // total fill units that hit a full cell or missed
    this.hitCell = -1;          // cell under the footprint centre this step
    this.lastDelta = 0;         // fill01 delta this step
    this.assisting = false;     // finish assist engaged
    this.assistCell = -1;       // cell the assist has locked on to (prevents thrash)
    this.focus = 0;             // 0 = wide fan, 1 = tight jet — driven by the aim stick
    this.manual = 0;            // 0 = auto sweep in charge, 1 = player aiming absolutely
    this.pinned = false;        // harness is holding fill01 constant for a shot
    this.milestoneMask = 0;     // bitmask of milestones already announced
  }

  bind(potholeId, cells) {
    this.potholeId = potholeId;
    this.cells = cells;
    this.active = true;
    this.elapsed = 0;
    this.sweepPhase = 0;
    this.delivered = 0;
    this.wasted = 0;
    this.hitCell = -1;
    this.lastDelta = 0;
    this.assisting = false;
    this.assistCell = -1;
    this.focus = 0;
    this.manual = 0;
    this.pinned = false;
    this.aimX = 0; this.aimY = 0;
    this.targetX = 0; this.targetY = 0;
    this.recompute();
    // Seed the milestone mask from the fill we inherited, so re-entering a half-done hole
    // does not re-announce milestones you already crossed.
    this.milestoneMask = 0;
  }

  release() {
    this.active = false;
    this.potholeId = -1;
    this.cells = null;
  }

  recompute() {
    const cells = this.cells;
    if (!cells) { this.fill01 = 0; this.minCell = 0; return; }
    let sum = 0;
    let lo = 1;
    for (let i = 0; i < cells.length; i++) {
      const v = cells[i];
      sum += v;
      if (v < lo) lo = v;
    }
    this.fill01 = sum / cells.length;
    this.minCell = lo;
  }
}

/**
 * The model itself. Stateless with respect to any one pothole — it takes a session and a
 * dt and advances it. Everything it reads comes from the tuning block.
 */
export class RepairModel {
  constructor(tuning) {
    this.t = tuning;
    this.grid = new CellGrid(tuning.rings, tuning.sectors);
    this._sprayR2 = tuning.sprayRadius * tuning.sprayRadius;
  }

  /** Allocate a cell buffer for one pothole. */
  makeCells() { return new Float32Array(this.grid.count); }

  /**
   * Rate divider for a hole of `radiusMul` x the reference radius. A bigger hole holds
   * proportionally more asphalt, so the same spray bar takes proportionally longer — see
   * TUNING.pothole.radiusRef. Lives here so the game and `tools/gp-repair.mjs` cannot drift.
   */
  volumeRate(radiusMul, pot) {
    const e = pot ? pot.volumeExponent : 1.8;
    if (!(radiusMul > 0)) return 1;
    return 1 / Math.pow(radiusMul, e);
  }

  /**
   * WHAT WOULD THIS STEP HAVE DELIVERED? Used only by a harness-PINNED session, which holds
   * the meter still for a shot but must still tell `fx` and `audio` how much material is
   * landing. Reporting a hard zero there is what made `pothole-fill` render the weakest
   * possible jet — every downstream intensity term is scaled by `delta` — so the shot showed
   * a nozzle doing nothing beside fx's ejecta and read as a demolition.
   *
   * Sprays for real, reads the delta, then puts the cells and the counters back exactly as
   * they were. One preallocated scratch buffer, no allocation per step.
   */
  probeDelta(session, dt, rateMul) {
    const cells = session.cells;
    if (!cells) return 0;
    if (!this._probe || this._probe.length !== cells.length) this._probe = new Float32Array(cells.length);
    this._probe.set(cells);
    const d0 = session.delivered, w0 = session.wasted, f0 = session.fill01, m0 = session.minCell;
    this.spray(session, dt, rateMul);
    const delta = session.lastDelta;
    cells.set(this._probe);
    session.delivered = d0; session.wasted = w0;
    session.fill01 = f0; session.minCell = m0; session.lastDelta = 0;
    return delta;
  }

  /**
   * Choose where the spray bar WANTS to be this step.
   *
   * Three regimes, in priority order:
   *   1. player aiming (|nozzle| past manualLo) — the stick is ABSOLUTE aim over the hole.
   *   2. finish assist (fill01 past finishAssistAt) — homes on the least-filled cell so the
   *      last 10% never turns into a hunt.
   *   3. automatic rosette — this is a truck-mounted spray bar, it oscillates on its own.
   */
  aimTarget(session, nozzleX, nozzleY, out) {
    const t = this.t;
    const g = this.grid;
    let tx, ty;

    /**
     * ROUND 12 — THE STICK STOPPED REPLACING THE ASSIST AND STARTED MODIFYING IT.
     *
     * The old rule was "past `assistYieldAt` the assist gets out of the way", on the theory
     * that two controllers hunting the same cell fight each other. Measured on
     * `tools/gp-repair.mjs`, that made the stick strictly self-harming: touching it turned off
     * the only thing that closes the last 26 %, so hand-aiming stalled at 87.4 % while holding
     * the trigger finished in 3.6 s.
     *
     * Swept the whole tuning space looking for numbers that let a player out-score the assist
     * and there are none, for a reason no knob can fix: **the assist already plays the search
     * optimally.** It sees every cell, picks the lowest, locks it, and never mis-aims. Asking
     * the player to beat it at its own job is asking them to beat a solver.
     *
     * So the two controllers now do DIFFERENT jobs, which is also what the tuning comments
     * always claimed they did:
     *   · the ASSIST finds the gap — it owns WHERE, and it is welcome to be good at it;
     *   · the STICK owns HOW — commit and the fan narrows to a jet that wastes far less
     *     asphalt on cells that are already full, plus a small nudge on the assist's choice.
     * Skill is now a decision (commit to precision, trade a little speed for a lot of
     * quality), not a search you have to win against a machine. Below `finishAssistAt` there
     * is no gap to find yet, so the stick keeps ABSOLUTE aim and steers the work area.
     */
    const assisting = session.fill01 >= t.finishAssistAt || session.elapsed >= t.assistAfterSeconds;

    if (assisting) {
      session.assisting = true;
      const cells = session.cells;
      let lo = Infinity, loIdx = 0;
      for (let i = 0; i < cells.length; i++) {
        if (cells[i] < lo) { lo = cells[i]; loIdx = i; }
      }
      // LOCK. Without this the bar ping-pongs across the hole chasing whichever cell is a
      // hair lower this frame, never converges, and the last 10% drags forever — which is
      // exactly the PowerWash failure we are here to avoid. Hold the current cell until it
      // is essentially done, or until something is meaningfully worse.
      const held = session.assistCell;
      if (held < 0 || cells[held] >= t.assistReleaseAt || cells[held] - lo > t.assistSwitchMargin) {
        session.assistCell = loIdx;
      }
      const c = session.assistCell;
      tx = g.cx[c];
      ty = g.cy[c];
      // Hydraulic slop — see TUNING.repair.assistSlop. Committing with the stick removes it.
      const slop = t.assistSlop * (1 - session.focus);
      if (slop > 0) {
        tx += Math.cos(session.sweepPhase * t.assistSlopHz) * slop;
        ty += Math.sin(session.sweepPhase * t.assistSlopHz * 1.31) * slop;
      }
    } else {
      session.assisting = false;
      session.assistCell = -1;
      // Rosette sweep. The radius is pushed through sqrt so the bar spends time
      // proportional to AREA rather than to radius — otherwise the middle gets four passes
      // for every one the rim gets, and the rim is where the meter stalls.
      const p = session.sweepPhase;
      const s01 = 0.5 + 0.5 * Math.sin(p * t.sweepRadial);
      const rr = t.sweepRadiusBase + t.sweepRadiusAmp * Math.sqrt(s01);
      const th = p * t.sweepAngular;
      tx = rr * Math.cos(th);
      ty = rr * Math.sin(th);
    }

    /**
     * HANDOVER. The stick is ABSOLUTE aim, not an offset on the moving sweep — an offset
     * from a target that is itself orbiting is unaimable, you can never put the jet on the
     * cell you are looking at. Push the stick right and the jet goes to the right side of
     * the hole. `manual` blends smoothly so a resting stick never twitches the bar.
     */
    const m = session.manual;
    if (m > 0) {
      if (assisting) {
        // The assist owns WHERE. The stick only nudges it, so committing to the jet cannot
        // walk it off the cell that actually needs filling.
        tx += nozzleX * t.manualNudge;
        ty += nozzleY * t.manualNudge;
      } else {
        /**
         * Bulk fill: the stick names a WORK CENTRE and the bar keeps orbiting it — see
         * TUNING.repair.manualOrbit. Parking the stick used to park the jet, which filled two
         * cells and then sprayed a full cell for the rest of the session.
         */
        const orbR = t.manualOrbit * (1 - m * t.manualOrbitShrink);
        const oth = session.sweepPhase * t.sweepAngular;
        const mx = nozzleX * t.manualReach + Math.cos(oth) * orbR;
        const my = nozzleY * t.manualReach + Math.sin(oth) * orbR;
        tx += (mx - tx) * m;
        ty += (my - ty) * m;
      }
    }

    const len = Math.sqrt(tx * tx + ty * ty);
    if (len > t.aimClamp) {
      const k = t.aimClamp / len;
      tx *= k; ty *= k;
    }
    out.x = tx; out.y = ty;
    return out;
  }

  /**
   * Deposit hot asphalt for one fixed step.
   * Returns the fill01 delta. Mutates session.cells, session.delivered/wasted/hitCell.
   *
   * Cells already at 1 absorb nothing — parking the nozzle in one spot is pure waste, which
   * is what forces you to sweep.
   */
  spray(session, dt, rateMul) {
    const t = this.t;
    const g = this.grid;
    const cells = session.cells;
    const n = cells.length;

    /**
     * TWO NOZZLE MODES, blended by how hard you are pushing the aim stick.
     *   stick centred  → wide fan: covers ground fast, re-covers cells it has already
     *                    finished, wastes a lot of asphalt. This is the "just hold the
     *                    trigger" path and it must be able to finish the job.
     *   stick pushed   → tight jet: smaller footprint, higher rate at the point. This is
     *                    how you top up the one cell that is lagging without dumping
     *                    another 40 kg on the cells beside it.
     * Waste is what separates a PERFECT from a pass, so the stick is where the skill lives.
     */
    const focus = session.focus;
    const sprayR = t.sprayRadius + (t.sprayRadiusFocused - t.sprayRadius) * focus;
    const r2 = sprayR * sprayR;
    const cutoff = r2 * t.sprayCutoff;
    // Sharper Gaussian as the jet narrows — see TUNING.repair.sprayFalloffFocused.
    const falloff = t.sprayFalloff + (t.sprayFalloffFocused - t.sprayFalloff) * focus;
    const ax = session.aimX, ay = session.aimY;
    const rate = (t.fillRate + (t.fillRateFocused - t.fillRate) * focus) * rateMul * dt;

    let delivered = 0;
    let wasted = 0;
    let bestF = 0;
    let bestCell = -1;

    for (let c = 0; c < n; c++) {
      const dx = g.cx[c] - ax;
      const dy = g.cy[c] - ay;
      const d2 = dx * dx + dy * dy;
      if (d2 > cutoff) continue;
      const f = Math.exp(-(d2 / r2) * falloff);
      if (f > bestF) { bestF = f; bestCell = c; }
      const add = rate * f;
      const cur = cells[c];
      const room = 1 - cur;
      /**
       * WHAT COUNTS AS WASTE. Spraying a cell that is ALREADY FULL is sloppiness and is
       * charged in full. The last drop that tops a cell OFF is not — it is the unavoidable
       * granularity of a 1/60 s step, and charging it made precision score worse than holding
       * the trigger: a tight jet always overflows the cell it is finishing, so the better you
       * aimed the more "waste" you booked. Efficiency has to reward hitting empty cells, not
       * punish finishing them.
       */
      if (room <= 0) { wasted += add; continue; }
      if (add <= room) { cells[c] = cur + add; delivered += add; }
      else { cells[c] = 1; delivered += room; }
    }

    session.delivered += delivered;
    session.wasted += wasted;
    session.hitCell = bestCell;

    const before = session.fill01;
    session.recompute();
    session.lastDelta = session.fill01 - before;
    return session.lastDelta;
  }

  /**
   * Advance a live session by one fixed step.
   * `nozzleX/Y` are the player's aim stick, -1..1. `rateMul` folds in weather.
   * Returns true when the repair is COMPLETE this step.
   */
  step(session, dt, nozzleX, nozzleY, rateMul, aimOut) {
    const t = this.t;
    session.elapsed += dt;
    session.sweepPhase += dt;

    // Math.sqrt, not Math.hypot — ARCHITECTURE.md §6, hypot is engine-variable.
    const mag = Math.sqrt(nozzleX * nozzleX + nozzleY * nozzleY);
    const span = t.manualHi - t.manualLo;
    let m = (mag - t.manualLo) / (span > 1e-6 ? span : 1);
    m = m < 0 ? 0 : m > 1 ? 1 : m;
    // smoothstep — no twitch off a resting stick — and capped, so the automatic bar always
    // keeps a slice of the authority. See TUNING.repair.manualMax.
    session.manual = m * m * (3 - 2 * m) * t.manualMax;
    session.focus = session.manual / t.manualMax;   // jet narrowness tracks player intent

    this.aimTarget(session, nozzleX, nozzleY, aimOut);
    session.targetX = aimOut.x;
    session.targetY = aimOut.y;

    // Exponential chase — the bar has mass, it does not teleport.
    const k = 1 - Math.exp(-t.aimRate * dt);
    session.aimX += (session.targetX - session.aimX) * k;
    session.aimY += (session.targetY - session.aimY) * k;

    if (session.pinned) {
      // Harness is holding the meter for a shot: the bar still sweeps and the spray still
      // reads, but the fill does not advance and cannot complete.
      session.hitCell = this.cellAt(session.aimX, session.aimY);
      session.lastDelta = 0;
      return false;
    }

    this.spray(session, dt, rateMul);
    return this.isComplete(session);
  }

  /** Nearest cell to a normalised disc point. Used when the fill is pinned. */
  cellAt(x, y) {
    const g = this.grid;
    let best = -1, bestD = Infinity;
    for (let c = 0; c < g.count; c++) {
      const dx = g.cx[c] - x, dy = g.cy[c] - y;
      const d = dx * dx + dy * dy;
      if (d < bestD) { bestD = d; best = c; }
    }
    return best;
  }

  /**
   * SNAP TO COMPLETE. The classic PowerWash frustration is the last stray percent; we refuse
   * to ship it. Once the aggregate is over snapFill AND no single cell is below snapMinCell,
   * the repair is done and pops.
   */
  isComplete(session) {
    const t = this.t;
    if (session.fill01 >= 0.995) return true;
    return session.fill01 >= t.snapFill && session.minCell >= t.snapMinCell;
  }

  /**
   * quality01 — how well you laid the asphalt, not how fast.
   *
   * Two terms, both of which the player controls and neither of which can be ground out by
   * spending more time (the snap ends the session the moment it is satisfied):
   *
   *   EFFICIENCY  delivered / (delivered + wasted). The wide fan re-covers cells it has
   *               already finished; the tight jet does not. A player who tops up the lagging
   *               cells with the stick wastes far less hot asphalt than one who parks on the
   *               trigger and lets the bar swing. This is the main skill term.
   *   COVERAGE    the worst cell at completion. Partly pinned by snapMinCell, so it carries
   *               the smaller weight — it is here so that a scrappy snap-finish reads as
   *               scrappy rather than identical to a clean one.
   */
  quality(session) {
    const t = this.t;
    const sprayed = session.delivered + session.wasted;
    const eff = sprayed > 1e-6 ? session.delivered / sprayed : 0;
    const effR = clamp01((eff - t.qualityEffLo) / Math.max(1e-6, t.qualityEffHi - t.qualityEffLo));

    /**
     * FLATNESS — the standard deviation of the finished cells, inverted.
     *
     * This replaces the old `minCell` coverage term, and the reason is measured rather than
     * aesthetic. Efficiency is `delivered / sprayed`, and since a finished hole always takes
     * exactly 60 units of delivery, efficiency reduces to `60 / (throughput x time)`: it is a
     * SPEED metric wearing a precision costume. The fastest line therefore always won it, the
     * snap guaranteed every run ended at the same coverage floor, and the two terms together
     * pinned every single completion in the game to quality **0.532** — the critic measured
     * that identical number on 15+ repairs and could not move it by playing better.
     *
     * Flatness is the thing the player actually controls and the thing a PowerWash player is
     * actually looking at: no missed streak. A tight jet spent on the laggard cells leaves an
     * even patch; a wide fan that snaps out the moment the floor is met leaves a ragged one.
     * It cannot be won by finishing sooner, which is exactly why it is here.
     */
    const cells = session.cells;
    let flat = 0;
    if (cells && cells.length) {
      const n = cells.length;
      let sum = 0;
      for (let i = 0; i < n; i++) sum += cells[i];
      const mean = sum / n;
      let v = 0;
      for (let i = 0; i < n; i++) { const d = cells[i] - mean; v += d * d; }
      const sd = Math.sqrt(v / n);
      flat = clamp01((t.qualityFlatLo - sd) / Math.max(1e-6, t.qualityFlatLo - t.qualityFlatHi));
    }
    return clamp01(t.qualityEffWeight * effR + (1 - t.qualityEffWeight) * flat);
  }

  /** Fraction of sprayed asphalt that actually landed in an unfilled cell. */
  efficiency(session) {
    const sprayed = session.delivered + session.wasted;
    return sprayed > 1e-6 ? session.delivered / sprayed : 0;
  }

  isPerfect(quality01) { return quality01 >= this.t.perfectAt; }

  /**
   * Which milestones (25/50/75/90) were crossed this step. Returns a bitmask of NEW ones
   * and updates the session mask so each fires exactly once.
   */
  newMilestones(session) {
    const ms = this.t.milestones;
    const pct = session.fill01 * 100;
    let fresh = 0;
    for (let i = 0; i < ms.length; i++) {
      const bit = 1 << i;
      if ((session.milestoneMask & bit) === 0 && pct >= ms[i]) {
        session.milestoneMask |= bit;
        fresh |= bit;
      }
    }
    return fresh;
  }

  /**
   * Fill a cell buffer to a requested mean with STABLE unevenness, so a harness-forced
   * "62% done" pothole actually looks like a half-finished repair instead of a flat wash.
   * `noise` is a per-pothole fixed pattern with mean ~0.
   */
  fillTo(cells, mean01, noise) {
    const n = cells.length;
    // Two passes: apply the pattern, then correct the mean so fill01 is exactly what was asked.
    for (let i = 0; i < n; i++) cells[i] = clamp01(mean01 + noise[i]);
    for (let pass = 0; pass < 6; pass++) {
      let sum = 0;
      for (let i = 0; i < n; i++) sum += cells[i];
      const err = mean01 - sum / n;
      if (Math.abs(err) < 1e-4) break;
      for (let i = 0; i < n; i++) cells[i] = clamp01(cells[i] + err);
    }
  }
}

export { clamp01 };
